import { useState } from 'react';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import EmojiEvents from '@mui/icons-material/EmojiEvents';
import Info from '@mui/icons-material/Info';
import DragIndicator from '@mui/icons-material/DragIndicator';

// ─── Design tokens ──────────────────────────────────────────────────────────

const CERT = {
    none:   { label: 'None',   bg: '#F9FAFB', border: '#E5E7EB', text: '#6B7280', badge: '#E5E7EB', badgeText: '#374151', dot: '#9CA3AF' },
    bronze: { label: 'Bronze', bg: '#FFFBEB', border: '#FCD34D', text: '#92400E', badge: '#FCD34D', badgeText: '#78350F', dot: '#F59E0B' },
    silver: { label: 'Silver', bg: '#F8FAFC', border: '#94A3B8', text: '#1E293B', badge: '#CBD5E1', badgeText: '#1E293B', dot: '#94A3B8' },
    gold:   { label: 'Gold',   bg: '#FFFDF0', border: '#FBBF24', text: '#78350F', badge: '#FDE68A', badgeText: '#78350F', dot: '#F59E0B' },
};

const OPERATORS = [
    { value: 'lt',  label: '< less than'          },
    { value: 'lte', label: '≤ less than or equal'  },
    { value: 'gt',  label: '> greater than'        },
    { value: 'gte', label: '≥ greater than or equal'},
    { value: 'eq',  label: '= equals exactly'      },
];

const OP_SYMBOL = { lt: '<', lte: '≤', gt: '>', gte: '≥', eq: '=' };

// ─── Condition helpers ──────────────────────────────────────────────────────

function emptyClause()      { return { op: 'gte', value: '' }; }
function conditionToEntries(cond) {
    if (!cond || typeof cond !== 'object') return [emptyClause()];
    const entries = Object.entries(cond).map(([op, v]) => ({ op, value: String(v) }));
    return entries.length ? entries : [emptyClause()];
}
function entriesToCondition(entries) {
    const out = {};
    for (const { op, value } of entries) {
        const n = parseFloat(value);
        if (op && value !== '' && !isNaN(n)) out[op] = n;
    }
    return Object.keys(out).length ? out : null;
}
function ruleToState(rule) {
    return {
        completenessEntries: conditionToEntries(rule?.completeness),
        includeRisk:         !!(rule?.riskScore),
        riskEntries:         rule?.riskScore ? conditionToEntries(rule.riskScore) : [emptyClause()],
        certification:       rule?.certification || 'none',
    };
}
function stateToRule(s) {
    const completeness = entriesToCondition(s.completenessEntries);
    const riskScore    = s.includeRisk ? entriesToCondition(s.riskEntries) : undefined;
    return { completeness, ...(riskScore ? { riskScore } : {}), certification: s.certification };
}

// ─── Preview evaluator (mirrors backend) ───────────────────────────────────

function evalCond(cond, v) {
    if (!cond) return false;
    for (const [op, t] of Object.entries(cond)) {
        const n = Number(t);
        if (op === 'lt'  && !(v <  n)) return false;
        if (op === 'lte' && !(v <= n)) return false;
        if (op === 'gt'  && !(v >  n)) return false;
        if (op === 'gte' && !(v >= n)) return false;
        if (op === 'eq'  && !(v === n)) return false;
    }
    return true;
}
function evalRules(rules, risk, comp) {
    for (const r of rules) {
        if (!r?.completeness) continue;
        if (!evalCond(r.completeness, comp)) continue;
        if (r.riskScore && !evalCond(r.riskScore, risk)) continue;
        return r.certification || 'none';
    }
    return 'none';
}

// ─── Shared small components ────────────────────────────────────────────────

function Badge({ cert }) {
    const t = CERT[cert] || CERT.none;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '2px 10px', borderRadius: '20px',
            background: t.badge, color: t.badgeText,
            fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
            border: `1px solid ${t.border}`,
        }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: t.dot, flexShrink: 0 }} />
            {t.label}
        </span>
    );
}

function SectionLabel({ children, required }) {
    return (
        <div style={{ fontSize: '11px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            {children}
            {required && <span style={{ color: '#EF4444' }}>*</span>}
        </div>
    );
}

// ─── ConditionBuilder ───────────────────────────────────────────────────────

function ConditionBuilder({ entries, onChange }) {
    const update = (i, field, val) =>
        onChange(entries.map((e, idx) => idx === i ? { ...e, [field]: val } : e));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {entries.map((entry, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Operator */}
                    <select
                        value={entry.op}
                        onChange={e => update(i, 'op', e.target.value)}
                        style={{
                            flex: '0 0 auto', height: '34px', padding: '0 8px',
                            border: '1.5px solid #E5E7EB', borderRadius: '6px',
                            fontSize: '13px', background: '#fff', color: '#374151',
                            cursor: 'pointer', outline: 'none',
                        }}
                    >
                        {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>

                    {/* Value */}
                    <div style={{ position: 'relative', flex: '1' }}>
                        <input
                            type="number" min="0" max="100" placeholder="0 – 100"
                            value={entry.value}
                            onChange={e => update(i, 'value', e.target.value)}
                            style={{
                                width: '100%', height: '34px', padding: '0 32px 0 10px',
                                border: '1.5px solid #E5E7EB', borderRadius: '6px',
                                fontSize: '13px', background: '#fff', color: '#374151', outline: 'none',
                                boxSizing: 'border-box',
                            }}
                        />
                        <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: '#9CA3AF', pointerEvents: 'none' }}>%</span>
                    </div>

                    {/* Remove clause */}
                    {entries.length > 1 ? (
                        <button
                            type="button"
                            onClick={() => onChange(entries.filter((_, idx) => idx !== i))}
                            title="Remove clause"
                            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px', color: '#F87171', display: 'flex', alignItems: 'center', borderRadius: '4px', flexShrink: 0 }}
                        >
                            <Delete style={{ fontSize: '15px' }} />
                        </button>
                    ) : <span style={{ width: '23px', flexShrink: 0 }} />}
                </div>
            ))}

            {/* Add AND clause */}
            <button
                type="button"
                onClick={() => onChange([...entries, emptyClause()])}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '4px 10px', border: '1.5px dashed #D1D5DB', borderRadius: '6px',
                    fontSize: '12px', color: '#6B7280', background: 'none', cursor: 'pointer',
                    alignSelf: 'flex-start', marginTop: '2px',
                }}
            >
                <Add style={{ fontSize: '13px' }} /> AND clause
            </button>
        </div>
    );
}

// ─── RuleRow ─────────────────────────────────────────────────────────────────

function RuleRow({ state, index, total, onChange, onRemove, onMoveUp, onMoveDown }) {
    const t = CERT[state.certification] || CERT.none;

    return (
        <div style={{
            border: `1.5px solid ${t.border}`,
            borderRadius: '12px',
            background: t.bg,
            overflow: 'hidden',
            transition: 'box-shadow 0.15s',
        }}>
            {/* ── Header bar ─────────────────────────────────────────────── */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 16px',
                borderBottom: `1px solid ${t.border}`,
                background: `${t.bg}`,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <DragIndicator style={{ fontSize: '18px', color: '#D1D5DB', cursor: 'grab' }} />
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.04em' }}>
                        RULE {index + 1}
                    </span>
                    <Badge cert={state.certification} />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <button type="button" disabled={index === 0} onClick={onMoveUp} title="Move up"
                        style={{ border: 'none', background: 'none', cursor: index === 0 ? 'default' : 'pointer', padding: '4px', borderRadius: '6px', opacity: index === 0 ? 0.3 : 0.7, color: '#374151', display: 'flex' }}>
                        <KeyboardArrowUp style={{ fontSize: '18px' }} />
                    </button>
                    <button type="button" disabled={index === total - 1} onClick={onMoveDown} title="Move down"
                        style={{ border: 'none', background: 'none', cursor: index === total - 1 ? 'default' : 'pointer', padding: '4px', borderRadius: '6px', opacity: index === total - 1 ? 0.3 : 0.7, color: '#374151', display: 'flex' }}>
                        <KeyboardArrowDown style={{ fontSize: '18px' }} />
                    </button>
                    <div style={{ width: '1px', height: '18px', background: '#E5E7EB', margin: '0 6px' }} />
                    <button type="button" onClick={onRemove} title="Delete rule"
                        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px', borderRadius: '6px', color: '#F87171', display: 'flex' }}>
                        <Delete style={{ fontSize: '18px' }} />
                    </button>
                </div>
            </div>

            {/* ── Rule body ──────────────────────────────────────────────── */}
            <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', alignItems: 'start' }}>

                {/* Completeness */}
                <div style={{ background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: '8px', padding: '14px' }}>
                    <SectionLabel required>Completeness</SectionLabel>
                    <ConditionBuilder
                        entries={state.completenessEntries}
                        onChange={entries => onChange({ ...state, completenessEntries: entries })}
                    />
                </div>

                {/* Risk Score */}
                <div style={{ background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: '8px', padding: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <SectionLabel>Risk Score</SectionLabel>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none' }}>
                            <div
                                onClick={() => onChange({ ...state, includeRisk: !state.includeRisk })}
                                style={{
                                    width: '32px', height: '18px', borderRadius: '9px', position: 'relative',
                                    background: state.includeRisk ? '#6366F1' : '#D1D5DB',
                                    cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
                                }}
                            >
                                <span style={{
                                    position: 'absolute', top: '2px',
                                    left: state.includeRisk ? '16px' : '2px',
                                    width: '14px', height: '14px', borderRadius: '50%',
                                    background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                }} />
                            </div>
                            <span style={{ fontSize: '12px', color: '#6B7280' }}>{state.includeRisk ? 'On' : 'Off'}</span>
                        </label>
                    </div>
                    {state.includeRisk ? (
                        <ConditionBuilder
                            entries={state.riskEntries}
                            onChange={entries => onChange({ ...state, riskEntries: entries })}
                        />
                    ) : (
                        <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '4px 0 0', lineHeight: 1.5 }}>
                            Risk score is not considered for this rule.
                        </p>
                    )}
                </div>

                {/* Certification result */}
                <div style={{ background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: '8px', padding: '14px' }}>
                    <SectionLabel required>Award Certification</SectionLabel>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        {Object.entries(CERT).map(([key, tok]) => {
                            const selected = state.certification === key;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => onChange({ ...state, certification: key })}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
                                        border: selected ? `2px solid ${tok.dot}` : '1.5px solid #E5E7EB',
                                        background: selected ? tok.bg : '#fff',
                                        transition: 'all 0.15s',
                                        boxShadow: selected ? `0 0 0 3px ${tok.badge}` : 'none',
                                    }}
                                >
                                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: tok.dot, flexShrink: 0 }} />
                                    <span style={{ fontSize: '13px', fontWeight: selected ? 700 : 500, color: tok.text }}>{tok.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ── Condition summary chip ─────────────────────────────────── */}
            <div style={{ padding: '0 16px 12px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {state.completenessEntries.filter(e => e.value !== '').map((e, i) => (
                    <span key={i} style={{ fontSize: '11px', padding: '3px 9px', borderRadius: '20px', background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                        completeness {OP_SYMBOL[e.op] || e.op} {e.value}%
                    </span>
                ))}
                {state.includeRisk && state.riskEntries.filter(e => e.value !== '').map((e, i) => (
                    <span key={i} style={{ fontSize: '11px', padding: '3px 9px', borderRadius: '20px', background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' }}>
                        risk {OP_SYMBOL[e.op] || e.op} {e.value}%
                    </span>
                ))}
            </div>
        </div>
    );
}

// ─── Preview table ───────────────────────────────────────────────────────────

const SAMPLES = [
    { comp: 0,   risk: 0,  label: '0%'   },
    { comp: 25,  risk: 30, label: '25%'  },
    { comp: 50,  risk: 30, label: '50%'  },
    { comp: 67,  risk: 30, label: '67%'  },
    { comp: 75,  risk: 30, label: '75%'  },
    { comp: 75,  risk: 70, label: '75% / high risk' },
    { comp: 85,  risk: 20, label: '85%'  },
    { comp: 100, risk: 20, label: '100%' },
    { comp: 100, risk: 70, label: '100% / high risk' },
];

function PreviewTable({ rules }) {
    const activeRules = rules.filter(r => r.completeness !== null);
    if (activeRules.length === 0) return null;

    return (
        <div style={{ marginTop: '20px', border: '1.5px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Info style={{ fontSize: '16px', color: '#6B7280' }} />
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Live Preview</span>
                <span style={{ fontSize: '12px', color: '#9CA3AF' }}>— how rules evaluate at sample values</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
                {SAMPLES.map((s, i) => {
                    const cert = evalRules(activeRules, s.risk, s.comp);
                    const tok  = CERT[cert] || CERT.none;
                    return (
                        <div key={i} style={{
                            padding: '10px 14px', borderRight: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: tok.bg,
                        }}>
                            <span style={{ fontSize: '12px', color: '#6B7280' }}>{s.label}</span>
                            <Badge cert={cert} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Main export ─────────────────────────────────────────────────────────────

export default function CertificationRulesBuilder({ rules = [], onChange }) {
    const [states, setStates] = useState(() => (rules || []).map(ruleToState));
    const [lastKey, setLastKey] = useState(() => JSON.stringify(rules || []));

    // Sync when parent re-initialises (e.g. module load on edit)
    const nextKey = JSON.stringify(rules || []);
    if (nextKey !== lastKey) {
        setLastKey(nextKey);
        setStates((rules || []).map(ruleToState));
    }

    const push = (next) => {
        setStates(next);
        onChange(next.map(stateToRule).filter(r => r.completeness !== null));
    };

    const add    = ()        => push([...states, ruleToState({})]);
    const remove = (i)       => push(states.filter((_, idx) => idx !== i));
    const update = (i, s)    => push(states.map((x, idx) => idx === i ? s : x));
    const move   = (i, dir)  => {
        const j = i + dir;
        if (j < 0 || j >= states.length) return;
        const next = [...states];
        [next[i], next[j]] = [next[j], next[i]];
        push(next);
    };

    const builtRules = states.map(stateToRule).filter(r => r.completeness !== null);

    return (
        <div>
            {/* ── Header ───────────────────────────────────────────────── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <EmojiEvents style={{ fontSize: '20px', color: '#F59E0B' }} />
                        <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#111827' }}>Certification Rules</h4>
                        <span style={{ fontSize: '11px', padding: '2px 8px', background: '#EFF6FF', color: '#3B82F6', borderRadius: '20px', border: '1px solid #BFDBFE', fontWeight: 600 }}>
                            {states.length} {states.length === 1 ? 'rule' : 'rules'}
                        </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '13px', color: '#6B7280', lineHeight: 1.5 }}>
                        Rules are evaluated <strong>top-to-bottom</strong>. First matching rule wins.
                        Completeness is required; risk score is optional per rule.
                    </p>
                </div>
                <button
                    type="button" onClick={add}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '9px 18px', borderRadius: '8px', cursor: 'pointer',
                        background: '#111827', color: '#fff', border: 'none',
                        fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                    }}
                >
                    <Add style={{ fontSize: '16px' }} /> Add Rule
                </button>
            </div>

            {/* ── Rule ordering hint ───────────────────────────────────── */}
            {states.length > 1 && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '8px 14px', borderRadius: '8px',
                    background: '#FFFBEB', border: '1px solid #FCD34D',
                    marginBottom: '16px', fontSize: '12px', color: '#92400E',
                }}>
                    <Info style={{ fontSize: '15px', flexShrink: 0 }} />
                    Rules are checked in order — place more specific conditions first to avoid them being shadowed by broader ones.
                </div>
            )}

            {/* ── Empty state ──────────────────────────────────────────── */}
            {states.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '48px 24px',
                    border: '2px dashed #E5E7EB', borderRadius: '12px',
                    background: '#FAFAFA',
                }}>
                    <EmojiEvents style={{ fontSize: '40px', color: '#D1D5DB', display: 'block', margin: '0 auto 12px' }} />
                    <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: '#6B7280' }}>No certification rules yet</p>
                    <p style={{ margin: 0, fontSize: '13px', color: '#9CA3AF' }}>
                        Click <strong>Add Rule</strong> to define when a module earns bronze, silver, or gold.
                    </p>
                </div>
            ) : (
                /* ── Rule list ─────────────────────────────────────────── */
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {states.map((s, i) => (
                        <RuleRow
                            key={i} index={i} total={states.length} state={s}
                            onChange={(next) => update(i, next)}
                            onRemove={() => remove(i)}
                            onMoveUp={() => move(i, -1)}
                            onMoveDown={() => move(i, 1)}
                        />
                    ))}
                </div>
            )}

            {/* ── Preview ─────────────────────────────────────────────── */}
            <PreviewTable rules={builtRules} />
        </div>
    );
}
